import useHttpProvider from "@/common/hooks/use-http";
import { AuthenticationProvider } from "@/common/providers/authentication";
import { trackAddToCart } from "@/common/services/analytics";
import { useSiteSettings } from "@/common/providers/site-settings";
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AddCartItemPayload, CartItem } from "../models/cart";
import * as CartService from "../services/cart";

const GUEST_CART_STORAGE_KEY = "reelrewards_guest_cart_v1";

function isInsufficientStock(err: unknown): boolean {
	return (
		!!err &&
		typeof err === "object" &&
		"detail" in err &&
		(err as { detail: unknown }).detail === "insufficient_stock"
	);
}

type CartContextValue = {
	isLoading: boolean;
	items: CartItem[];
	itemCount: number;
	subtotal: number;
	addItem: (payload: AddCartItemPayload) => Promise<void>;
	setQuantity: (variantId: number, quantity: number) => Promise<void>;
	removeItem: (variantId: number) => Promise<void>;
	clear: () => Promise<void>;
};

const CartContext = createContext<CartContextValue | null>(null);

function readGuestCart(): CartItem[] {
	try {
		const raw = window.sessionStorage.getItem(GUEST_CART_STORAGE_KEY);
		if (!raw) return [];
		const parsed = JSON.parse(raw) as CartItem[];
		return Array.isArray(parsed) ? parsed : [];
	} catch {
		return [];
	}
}

function writeGuestCart(items: CartItem[]) {
	try {
		window.sessionStorage.setItem(GUEST_CART_STORAGE_KEY, JSON.stringify(items));
	} catch {
		// ignore
	}
}

function clearGuestCart() {
	try {
		window.sessionStorage.removeItem(GUEST_CART_STORAGE_KEY);
	} catch {
		// ignore
	}
}

export function CartProvider({ children }: { children: React.ReactNode }) {
	const { http } = useHttpProvider();
	const { settings } = useSiteSettings();
	const cartEnabled = settings.storeEnabled && settings.cartEnabled;

	const [items, setItems] = useState<CartItem[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isAuthenticated, setIsAuthenticated] = useState(
		AuthenticationProvider.isAuthenticated
	);

	const itemCount = useMemo(
		() => items.reduce((sum, item) => sum + (item.quantity ?? 0), 0),
		[items]
	);

	const subtotal = useMemo(() => {
		return items.reduce((sum, item) => {
			const unit = Number(item.basePrice ?? 0) + Number(item.priceModifier ?? 0);
			return sum + unit * Number(item.quantity ?? 0);
		}, 0);
	}, [items]);

	const refreshFromServer = async () => {
		const cart = await CartService.getCart(http);
		setItems(cart.items || []);
	};

	useEffect(() => {
		let cancelled = false;

		// If the cart feature is disabled, don't do any bootstrapping or server calls.
		if (!cartEnabled) {
			setItems([]);
			setIsLoading(false);
			return;
		}

		const bootstrap = async (authenticated: boolean) => {
			setIsLoading(true);
			try {
				if (authenticated) {
					// Load server cart first.
					await refreshFromServer();

					// Merge any guest cart (session) into the user cart (server).
					const guestItems = readGuestCart();
					if (guestItems.length > 0) {
						const failed: string[] = [];
						for (const item of guestItems) {
							try {
								await CartService.addCartItem(
									http,
									item.variantId,
									Math.max(1, Number(item.quantity ?? 1))
								);
							} catch {
								failed.push(item.productName || `variant ${item.variantId}`);
							}
						}
						clearGuestCart();
						await refreshFromServer();
						if (failed.length > 0) {
							toast.error(
								`Couldn't add to cart (out of stock or unavailable): ${failed.join(", ")}`
							);
						}
					}
				} else {
					setItems(readGuestCart());
				}
			} catch {
				if (!cancelled) setItems([]);
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};

		const onAuthChanged = () => {
			const nextAuth = AuthenticationProvider.isAuthenticated;
			setIsAuthenticated(nextAuth);
			void bootstrap(nextAuth);
		};

		// Initial load.
		void bootstrap(AuthenticationProvider.isAuthenticated);

		// React to login/logout.
		window.addEventListener("reelrewards:auth-changed", onAuthChanged);

		return () => {
			cancelled = true;
			window.removeEventListener("reelrewards:auth-changed", onAuthChanged);
		};
	}, [http, cartEnabled]);

	const addItem = async (payload: AddCartItemPayload) => {
		if (!cartEnabled) {
			toast.error("Cart is currently disabled.");
			return;
		}

		const quantity = Math.max(1, Number(payload.quantity ?? 1));

		if (!payload.variantId) return;

		if (isAuthenticated) {
			try {
				await CartService.addCartItem(http, payload.variantId, quantity);
				await refreshFromServer();
				const unitPrice =
					Number(payload.basePrice ?? 0) + Number(payload.priceModifier ?? 0);
				trackAddToCart({
					content_name: payload.productName,
					content_type: "product",
					content_ids: [String(payload.variantId)],
					value: Number((unitPrice * quantity).toFixed(2)),
					currency: "AUD",
					num_items: quantity,
				});
				toast.success("Added to cart");
			} catch (err) {
				toast.error(
					isInsufficientStock(err)
						? "Not enough stock available."
						: "Could not add item to cart."
				);
			}
			return;
		}

		const existing = items.find((i) => i.variantId === payload.variantId);
		const currentQty = existing ? Number(existing.quantity ?? 0) : 0;
		const stock = Number(payload.stock ?? 0);
		if (stock <= 0 || currentQty + quantity > stock) {
			toast.error("Not enough stock available.");
			return;
		}

		setItems((prev) => {
			const existingPrev = prev.find((i) => i.variantId === payload.variantId);
			const next = existingPrev
				? prev.map((i) =>
						i.variantId === payload.variantId
							? { ...i, quantity: (i.quantity ?? 0) + quantity }
							: i
					)
				: [
						...prev,
						{
							variantId: payload.variantId,
							quantity,
							productId: payload.productId,
							productName: payload.productName,
							productDescription: payload.productDescription,
							productImageUrl: payload.productImageUrl,
							variantName: payload.variantName,
							sku: payload.sku,
							priceModifier: payload.priceModifier,
							basePrice: payload.basePrice,
							stock: payload.stock,
							lowStockThreshold: payload.lowStockThreshold,
							attributes: payload.attributes ?? [],
						},
					];

			writeGuestCart(next);
			return next;
		});
		const unitPrice =
			Number(payload.basePrice ?? 0) + Number(payload.priceModifier ?? 0);
		trackAddToCart({
			content_name: payload.productName,
			content_type: "product",
			content_ids: [String(payload.variantId)],
			value: Number((unitPrice * quantity).toFixed(2)),
			currency: "AUD",
			num_items: quantity,
		});
		toast.success("Added to cart");
	};

	const setQuantity = async (variantId: number, quantity: number) => {
		if (!cartEnabled) {
			toast.error("Cart is currently disabled.");
			return;
		}

		const nextQty = Math.max(0, Math.floor(quantity));

		if (isAuthenticated) {
			try {
				await CartService.setCartItemQuantity(http, variantId, nextQty);
				await refreshFromServer();
			} catch (err) {
				toast.error(
					isInsufficientStock(err)
						? "Not enough stock available."
						: "Could not update cart."
				);
			}
			return;
		}

		if (nextQty > 0) {
			const target = items.find((i) => i.variantId === variantId);
			const stock = Number(target?.stock ?? 0);
			if (stock <= 0 || nextQty > stock) {
				toast.error("Not enough stock available.");
				return;
			}
		}

		setItems((prev) => {
			const next =
				nextQty <= 0
					? prev.filter((i) => i.variantId !== variantId)
					: prev.map((i) =>
							i.variantId === variantId ? { ...i, quantity: nextQty } : i
						);
			writeGuestCart(next);
			return next;
		});
	};

	const removeItem = async (variantId: number) => {
		if (!cartEnabled) {
			toast.error("Cart is currently disabled.");
			return;
		}

		if (isAuthenticated) {
			try {
				await CartService.removeCartItem(http, variantId);
				await refreshFromServer();
			} catch {
				toast.error("Could not remove item.");
			}
			return;
		}

		setItems((prev) => {
			const next = prev.filter((i) => i.variantId !== variantId);
			writeGuestCart(next);
			return next;
		});
	};

	const clear = async () => {
		if (!cartEnabled) {
			toast.error("Cart is currently disabled.");
			return;
		}

		if (isAuthenticated) {
			try {
				await CartService.clearCart(http);
				await refreshFromServer();
			} catch {
				toast.error("Could not clear cart.");
			}
			return;
		}

		setItems(() => {
			clearGuestCart();
			return [];
		});
	};

	const value: CartContextValue = {
		isLoading,
		items,
		itemCount,
		subtotal,
		addItem,
		setQuantity,
		removeItem,
		clear,
	};

	return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
	const ctx = useContext(CartContext);
	if (!ctx) {
		throw new Error("useCart must be used within CartProvider");
	}
	return ctx;
}
